import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { withWorkspaceFilter, getActiveWorkspaceId } from "@/utils/workspace-helpers";

export interface BusinessCentralIntegration {
  id: string;
  company_name: string | null;
  company_id: string | null;
  environment_name: string;
  tenant_id: string;
  is_active: boolean;
  created_at: string;
  token_expires_at: string | null;
}

export function useBusinessCentralIntegration() {
  const useHasActiveBusinessCentralQuery = () => {
    return useQuery({
      queryKey: ["has-active-business-central", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { data, error } = await withWorkspaceFilter(
          supabase.from("business_central_integrations")
          .select("id")
          .eq("is_active", true)
          .limit(1)
        );

        if (error) {
          throw error;
        }

        return data && data.length > 0;
      },
    });
  };

  const useLastSyncTimeQuery = () => {
    return useQuery({
      queryKey: ["business-central-last-sync", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await withWorkspaceFilter(
          supabase.from("business_central_detection_runs")
          .select("run_at")
          .order("run_at", { ascending: false })
          .limit(1)
          .single()
        );

        if (error) {
          if (error.code === "PGRST116") {
            return null;
          }
          throw error;
        }

        return data?.run_at ? new Date(data.run_at) : null;
      },
      refetchInterval: 60000, // Refetch every minute
    });
  };

  const useBusinessCentralIntegrationsQuery = () => {
    return useQuery({
      queryKey: ["business-central-integrations", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await withWorkspaceFilter(
          supabase.from("business_central_integrations")
          .select("*")
          .order("created_at", { ascending: false })
        );

        if (error) throw error;
        return (data || []) as BusinessCentralIntegration[];
      },
    });
  };

  const useActiveBusinessCentralIntegrationsQuery = () => {
    return useQuery({
      queryKey: ["business-central-integrations-active", getActiveWorkspaceId()],
      queryFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await withWorkspaceFilter(
          supabase.from("business_central_integrations")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
        );

        if (error) throw error;
        return (data || []) as BusinessCentralIntegration[];
      },
    });
  };

  return {
    useHasActiveBusinessCentralQuery,
    useLastSyncTimeQuery,
    useBusinessCentralIntegrationsQuery,
    useActiveBusinessCentralIntegrationsQuery,
  };
}
